import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { AuthContext } from '@/features/auth/auth-context';
import { ApiError, type ApiErrorKey } from '@/services/api/errors';
import { UserApi } from '@/services/api/user-api';
import type { UserProfileSettings, UserProfileUpdateRequest } from '@/services/api/types';

export interface UseUserProfileSettingsResult {
  data: UserProfileSettings | null;
  loading: boolean;
  /** Translatable error key when the initial fetch failed, or null. */
  loadError: ApiErrorKey | null;
  saving: boolean;
  /** Translatable error key when the last save attempt failed, or null. */
  saveError: ApiErrorKey | null;
  saved: boolean;
  save: (update: UserProfileUpdateRequest) => Promise<boolean>;
  clearSaveError: () => void;
}

export function useUserProfileSettings(user?: UserApi): UseUserProfileSettingsResult {
  const auth = useContext(AuthContext);

  const injectedApi = useRef(user);
  useEffect(() => {
    injectedApi.current = user;
  }, [user]);
  const realApiRef = useRef<UserApi | null>(null);

  const getApi = useCallback((): UserApi => {
    if (injectedApi.current) return injectedApi.current;
    const client = auth?.client;
    if (!client) throw new ApiError('unknown', 'generic');
    if (!realApiRef.current) realApiRef.current = new UserApi(client);
    return realApiRef.current;
  }, [auth]);

  const [data, setData] = useState<UserProfileSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<ApiErrorKey | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<ApiErrorKey | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const controller = new AbortController();

    void (async () => {
      try {
        const result = await getApi().getProfile(controller.signal);
        if (controller.signal.aborted) return;
        setData(result);
        setLoadError(null);
      } catch (err) {
        if (err instanceof ApiError && err.kind === 'aborted') return;
        setLoadError(err instanceof ApiError ? err.key : 'generic');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [getApi]);

  const save = useCallback(
    async (update: UserProfileUpdateRequest): Promise<boolean> => {
      setSaving(true);
      setSaveError(null);
      try {
        const result = await getApi().updateProfile(update);
        setData(result);
        setSaved(true);
        return true;
      } catch (err) {
        setSaveError(err instanceof ApiError ? err.key : 'generic');
        return false;
      } finally {
        setSaving(false);
      }
    },
    [getApi],
  );

  const clearSaveError = useCallback(() => setSaveError(null), []);

  return { data, loading, loadError, saving, saveError, saved, save, clearSaveError };
}
